<template>
  <div class="ms-integration-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">

        <div class="page-header">
          <div class="page-header-left">
            <div class="page-icon">
              <img src="@/assets/microsoft-color.svg" style="width:28px;height:28px" alt="Microsoft" />
            </div>
            <div>
              <h1 class="page-title">Microsoft Integration</h1>
              <p class="page-subtitle">Connect your Microsoft account to use Excel Online and OneDrive nodes in your WhatsApp flows.</p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading connected accounts...</p>
        </div>

        <div v-else class="connections-section">

          <!-- Info banner -->
          <div class="info-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div>
              <strong>How it works</strong>
              <p>Each Microsoft account you connect becomes available inside Excel Online and OneDrive nodes in the Flow Builder. We request only the minimum permissions needed. You can disconnect at any time.</p>
            </div>
          </div>

          <!-- Connected accounts -->
          <div class="section-card">
            <div class="section-card-header">
              <h2>Connected Accounts</h2>
              <button @click="connectMicrosoft" class="btn-connect" :disabled="connecting">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {{ connecting ? 'Redirecting to Microsoft...' : 'Connect Microsoft Account' }}
              </button>
            </div>

            <div v-if="connections.length === 0" class="empty-state">
              <div class="empty-icon">
                <img src="@/assets/microsoft-color.svg" style="width:52px;height:52px;opacity:.25" alt="Microsoft" />
              </div>
              <h3>No Microsoft accounts connected yet</h3>
              <p>Click <strong>Connect Microsoft Account</strong> to authorize access to your Excel files and OneDrive.</p>
            </div>

            <div v-else class="connections-list">
              <div v-for="conn in connections" :key="conn.id" class="connection-item">
                <div class="connection-avatar">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div class="connection-info">
                  <div class="connection-email">{{ conn.email }}</div>
                  <div v-if="conn.display_name && conn.display_name !== conn.email" class="connection-name">{{ conn.display_name }}</div>
                  <div class="connection-scopes">
                    <span class="scope-badge scope-excel">Excel</span>
                    <span class="scope-badge scope-onedrive">OneDrive</span>
                  </div>
                  <div class="connection-date">Connected {{ formatDate(conn.created_at) }}</div>
                </div>
                <div class="connection-actions">
                  <button
                    @click="disconnectConfirm(conn)"
                    class="btn-disconnect"
                    :disabled="disconnecting === conn.id"
                  >
                    {{ disconnecting === conn.id ? 'Disconnecting...' : 'Disconnect' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Permissions explanation -->
          <div class="section-card">
            <h2>What permissions are requested?</h2>
            <div class="scopes-list">
              <div class="scope-item">
                <div class="scope-icon scope-icon-excel">
                  <img src="@/assets/excel.svg" style="width:20px;height:20px" alt="Excel" />
                </div>
                <div>
                  <strong>Excel Online (Files.ReadWrite)</strong>
                  <p>Read and write Excel workbooks stored in OneDrive — used by Read Rows, Append Row, and Update Row nodes.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon scope-icon-onedrive">
                  <img src="@/assets/onedrive.svg" style="width:20px;height:20px" alt="OneDrive" />
                </div>
                <div>
                  <strong>OneDrive (Files.ReadWrite)</strong>
                  <p>Upload files and create folders in OneDrive — used by Upload File and Create Folder nodes.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon scope-icon-profile">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <strong>Profile (User.Read)</strong>
                  <p>Read your name and email address so we can display the connected account.</p>
                </div>
              </div>
            </div>
            <div class="privacy-note">
              We store your tokens securely and only use them to execute the actions you configure in your flows.
              Read our <a href="https://quali-d.com/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
            </div>
          </div>

        </div>

        <!-- Disconnect Confirm Modal -->
        <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Disconnect Microsoft Account</h3>
              <button @click="showDisconnectModal = false" class="btn-close">×</button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to disconnect <strong>{{ pendingDisconnect?.email }}</strong>?</p>
              <p class="modal-warning">Any flow nodes using this account will stop working until you reconnect.</p>
            </div>
            <div class="modal-footer">
              <button @click="showDisconnectModal = false" class="btn-secondary">Cancel</button>
              <button @click="confirmDisconnect" class="btn-danger" :disabled="!!disconnecting">
                {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'

export default {
  name: 'MicrosoftIntegrationView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      loading: true,
      connections: [],
      connecting: false,
      disconnecting: null,
      showDisconnectModal: false,
      pendingDisconnect: null
    }
  },

  mounted () {
    this.loadConnections()
    this.handleOAuthCallback()
  },

  methods: {
    async loadConnections () {
      this.loading = true
      try {
        const resp = await DashboardService.getMicrosoftConnections()
        this.connections = resp.connections || []
      } catch (e) {
        this.$toast?.error('Failed to load Microsoft connections')
      } finally {
        this.loading = false
      }
    },

    connectMicrosoft () {
      this.connecting = true
      const url = DashboardService.getMicrosoftOAuthUrl()
      window.location.href = url
    },

    disconnectConfirm (conn) {
      this.pendingDisconnect = conn
      this.showDisconnectModal = true
    },

    async confirmDisconnect () {
      if (!this.pendingDisconnect) return
      this.disconnecting = this.pendingDisconnect.id
      try {
        const resp = await DashboardService.disconnectMicrosoftConnection(this.pendingDisconnect.id)
        if (resp.success) {
          this.connections = this.connections.filter(c => c.id !== this.pendingDisconnect.id)
          this.$toast?.success('Microsoft account disconnected')
        } else {
          this.$toast?.error(resp.message || 'Failed to disconnect')
        }
      } catch (e) {
        this.$toast?.error('Failed to disconnect')
      } finally {
        this.disconnecting = null
        this.showDisconnectModal = false
        this.pendingDisconnect = null
      }
    },

    handleOAuthCallback () {
      const params = new URLSearchParams(window.location.search)
      if (params.get('connected') === '1') {
        const email = params.get('email') || 'your Microsoft account'
        this.$toast?.success(`Successfully connected ${email}`)
        window.history.replaceState({}, document.title, this.$route.path)
        this.loadConnections()
      }
      if (params.get('error')) {
        this.$toast?.error('Microsoft connection failed: ' + params.get('error'))
        window.history.replaceState({}, document.title, this.$route.path)
      }
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.ms-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary, #111827);
  color: var(--text-primary, #f9fafb);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.page-header { margin-bottom: 28px; }

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0078d4, #00b4f0);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--text-primary, #f9fafb);
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary, #9ca3af);
  font-size: 14px;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(0, 120, 212, 0.08);
  border: 1px solid rgba(0, 120, 212, 0.2);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  font-size: 13px;
  color: var(--text-primary, #f9fafb);
}

.info-banner svg { flex-shrink: 0; margin-top: 1px; color: #0078d4; }
.info-banner strong { display: block; margin-bottom: 3px; font-size: 13px; }
.info-banner p { margin: 0; color: var(--text-secondary, #9ca3af); }

.section-card {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  padding: 22px 24px;
  margin-bottom: 20px;
  max-width: 820px;
}

.section-card h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 18px;
  color: var(--text-primary, #f9fafb);
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-card-header h2 { margin: 0; }

.btn-connect {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #0078d4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.btn-connect:hover:not(:disabled) { background: #006cbe; }
.btn-connect:disabled { opacity: 0.6; cursor: not-allowed; }

.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: var(--text-secondary, #9ca3af);
}

.empty-icon { margin-bottom: 14px; }
.empty-state h3 { font-size: 15px; font-weight: 600; margin: 0 0 7px; color: var(--text-primary, #f9fafb); }
.empty-state p { font-size: 13px; margin: 0; }

.connections-list { display: flex; flex-direction: column; gap: 10px; }

.connection-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-color, #374151);
  border-radius: 10px;
}

.connection-avatar {
  width: 42px;
  height: 42px;
  background: rgba(0, 120, 212, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0078d4;
  flex-shrink: 0;
}

.connection-info { flex: 1; min-width: 0; }
.connection-email { font-size: 14px; font-weight: 600; color: var(--text-primary, #f9fafb); }
.connection-name { font-size: 12px; color: var(--text-secondary, #9ca3af); margin-top: 2px; }
.connection-date { font-size: 11px; color: var(--text-secondary, #6b7280); margin-top: 5px; }

.connection-scopes {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.scope-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.scope-excel    { background: rgba(33, 115, 70, 0.15);  color: #217346; }
.scope-onedrive { background: rgba(0, 120, 212, 0.15); color: #0078d4; }

.btn-disconnect {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-secondary, #9ca3af);
  border-radius: 7px;
  padding: 6px 13px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-disconnect:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
}

.btn-disconnect:disabled { opacity: 0.5; cursor: not-allowed; }

.scopes-list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 18px; }

.scope-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.scope-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.scope-icon-excel    { background: rgba(33, 115, 70, 0.12); }
.scope-icon-onedrive { background: rgba(0, 120, 212, 0.12); }
.scope-icon-profile  { background: rgba(0, 120, 212, 0.08); color: #0078d4; }

.scope-item strong { display: block; font-size: 13px; font-weight: 600; margin-bottom: 2px; color: var(--text-primary, #f9fafb); }
.scope-item p { margin: 0; font-size: 12px; color: var(--text-secondary, #9ca3af); }

.privacy-note {
  font-size: 12px;
  color: var(--text-secondary, #6b7280);
  border-top: 1px solid var(--border-color, #374151);
  padding-top: 14px;
}

.privacy-note a { color: #0078d4; text-decoration: none; }
.privacy-note a:hover { text-decoration: underline; }

.loading-state { text-align: center; padding: 60px 20px; color: var(--text-secondary, #9ca3af); }

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #374151);
  border-top-color: #0078d4;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 14px;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .main-content { margin-left: 0; }
  .page-content { padding: 16px; }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
}

.modal-content {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-color, #374151);
}

.modal-header h3 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary, #f9fafb); }

.btn-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 18px 22px;
  font-size: 14px;
  color: var(--text-primary, #f9fafb);
}

.modal-body p { margin: 0 0 8px; }
.modal-body p:last-child { margin-bottom: 0; }
.modal-warning { color: #f59e0b; font-size: 13px; }

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-color, #374151);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-primary, #f9fafb);
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover { background: rgba(255, 255, 255, 0.05); }

.btn-danger {
  background: #ef4444;
  border: none;
  color: white;
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-danger:hover:not(:disabled) { background: #dc2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
